import React, { useCallback, useEffect, useRef, useState } from "react";
import { PLATFORMS } from "../PlatformDefinitions";
import { PLATFORM_DISPLAY, getPlatformStatus, statusTone } from "../../lib/platformStatus";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Copy, History, Pencil, Trash2 } from "lucide-react";
import { inputCls, labelCls } from "./shared";

const DOT = { green: "bg-emerald-400", yellow: "bg-amber-400", red: "bg-red-500" };
const TONE_TEXT = { green: "Published", yellow: "Scheduled / processing", red: "Not published / needs attention" };
const validUrl = (u) => !u || /^https?:\/\//i.test(u);

/** Edits the same `platform_status` entries used by the Comprehensive Platform Matrix and the status strip. */
export default function ExternalLinksTab({ record, setPlatform, highlightKey, onUpdate }) {
  const refs = useRef({});
  const [captionEditor, setCaptionEditor] = useState(null);
  const [captionDrafts, setCaptionDrafts] = useState({});
  const [captionBusy, setCaptionBusy] = useState(null);
  useEffect(() => {
    if (highlightKey && refs.current[highlightKey]) refs.current[highlightKey].scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightKey]);

  const saveCaption = useCallback(async (platformId, caption) => {
    setCaptionBusy(platformId);
    try {
      const response = await api.post(`/api/video-central/${record.id}/captions/${platformId}`, { caption, action: "edited" });
      onUpdate?.({ id: record.id, platform_captions: { ...(record.platform_captions || {}), [platformId]: response.data } });
      setCaptionEditor(null);
      toast.success("Last used caption saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save caption");
    } finally {
      setCaptionBusy(null);
    }
  }, [onUpdate, record.id, record.platform_captions]);

  useEffect(() => {
    if (!captionEditor || captionBusy === captionEditor || captionDrafts[captionEditor] === undefined) return undefined;
    const savedCaption = record.platform_captions?.[captionEditor]?.caption || "";
    const caption = captionDrafts[captionEditor];
    if (caption === savedCaption) return undefined;
    const timeout = setTimeout(() => saveCaption(captionEditor, caption), 800);
    return () => clearTimeout(timeout);
  }, [captionDrafts, captionEditor, captionBusy, record.platform_captions, saveCaption]);

  const clearCaption = async (platformId) => {
    setCaptionBusy(platformId);
    try {
      const response = await api.delete(`/api/video-central/${record.id}/captions/${platformId}`);
      onUpdate?.({ id: record.id, platform_captions: { ...(record.platform_captions || {}), [platformId]: response.data } });
      setCaptionDrafts((previous) => ({ ...previous, [platformId]: "" }));
      toast.success("Last used caption cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear caption");
    } finally {
      setCaptionBusy(null);
    }
  };

  const clearAssetLink = async (platformId) => {
    try {
      const response = await api.patch(`/api/videos/${record.id}/platform-status/${platformId}`, { asset_ref: null });
      toast.success("Asset link cleared");
      onUpdate?.({ id: record.id, platform_status: response.data });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to clear link");
    }
  };

  const platforms = PLATFORMS;
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">Changes here update the Comprehensive Platform Matrix and the Platform Status strip instantly.</p>
      <div className="grid lg:grid-cols-2 gap-3">
        {platforms.map((p) => {
          const display = PLATFORM_DISPLAY.find((d) => d.key === p.id) || { key: p.id, label: p.label };
          const row = getPlatformStatus(record, display);
          const stored = record.platform_status?.[p.id] || {};
          const tone = statusTone(row.status);
          const hot = highlightKey === p.id;
          const fallbackStatus = p.id === "members_area" ? "" : p.contentType ? "not_posted" : "to_be_scheduled";
          const statusValue = p.statuses.some((status) => status.value === row.status) ? row.status : fallbackStatus;
          const savedCaption = record.platform_captions?.[p.id] || {};
          const caption = savedCaption.caption || "";
          const editingCaption = captionEditor === p.id;
          return (
            <div key={p.id} ref={(el) => { refs.current[p.id] = el; }} id={`platform-${p.id}`} data-testid={`ext-platform-${p.id}`}
              className={`rounded-xl border p-3 space-y-2.5 bg-black/40 transition ${hot ? "border-purple-400 ring-2 ring-purple-500/40" : "border-zinc-800"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${DOT[tone]}`} title={TONE_TEXT[tone]} aria-label={TONE_TEXT[tone]} />
                <span className="text-lg">{p.emoji}</span>
                <input aria-label="Platform name" className={`${inputCls} !py-1 font-semibold`} value={stored.label ?? p.label} onChange={(e) => setPlatform(p.id, { label: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2"><label className={labelCls}>URL</label>
                  <input className={`${inputCls} ${validUrl(row.url) ? "" : "border-red-500"}`} value={row.url || ""} placeholder="https://" onChange={(e) => setPlatform(p.id, { url: e.target.value })} />
                  {!validUrl(row.url) && <span className="text-[11px] text-red-400">URL must start with http:// or https://</span>}</div>
                <div><label className={labelCls}>Publication status</label>
                  <select className={inputCls} value={statusValue} onChange={(e) => setPlatform(p.id, { status: e.target.value })}>
                    {statusValue === "" && <option value="">Select access level</option>}
                    {p.statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select></div>
                <div><label className={labelCls}>Publish date</label>
                  <input type="date" className={inputCls} value={row.posted_date || ""} onChange={(e) => setPlatform(p.id, { posted_date: e.target.value })} /></div>
                <div><label className={labelCls}>Tier / product type</label>
                  <input className={inputCls} list="mv-tier-options" value={row.tier || ""} placeholder="e.g. VIP, PPV" onChange={(e) => setPlatform(p.id, { tier: e.target.value })} /></div>
                {p.hasPrice && <div><label className={labelCls}>Price (USD)</label>
                  <input type="number" step="0.01" className={inputCls} value={row.price ?? ""} onChange={(e) => setPlatform(p.id, { price: e.target.value })} /></div>}
                <div className="col-span-2"><label className={labelCls}>Notes</label>
                  <input className={inputCls} value={row.notes || ""} onChange={(e) => setPlatform(p.id, { notes: e.target.value })} placeholder="Post ID, remarks..." /></div>
              </div>
              {stored.asset_ref?.asset_id && <div className="flex items-center gap-2 text-xs text-zinc-400">
                <span>Linked to {stored.asset_ref.kind === "part" ? "Part" : "Cut"}</span>
                <code title={stored.asset_ref.asset_id}>{stored.asset_ref.asset_id.slice(0, 8)}</code>
                <button type="button" onClick={() => clearAssetLink(p.id)} className="underline hover:text-red-400">Clear link</button>
              </div>}
              <div className="border-t border-zinc-800/70 pt-2" data-testid={`last-used-caption-${p.id}`}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <label className={labelCls + " !mb-0"}>Last used caption</label>
                  {savedCaption.updated_at && <span className="text-[10px] text-zinc-500">{new Date(savedCaption.updated_at).toLocaleString()}</span>}
                </div>
                {!editingCaption && <p className="min-h-8 line-clamp-2 text-xs text-zinc-400" title={caption}>{caption || "No caption saved"}</p>}
                {editingCaption && <textarea rows={4} aria-label={`${p.label} caption`} value={captionDrafts[p.id] ?? caption}
                  onChange={(e) => setCaptionDrafts((previous) => ({ ...previous, [p.id]: e.target.value }))}
                  className="w-full resize-y rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs text-white focus:border-purple-500" />}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {!editingCaption && <button type="button" onClick={() => { setCaptionDrafts((previous) => ({ ...previous, [p.id]: caption })); setCaptionEditor(p.id); }} className="text-[10px] px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:text-white"><Pencil size={11} className="inline mr-1" />{caption ? "Edit caption" : "Add caption"}</button>}
                  {caption && <button type="button" onClick={() => { navigator.clipboard.writeText(caption); toast.success("Caption copied"); }} className="text-[10px] px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:text-white"><Copy size={11} className="inline mr-1" />Copy</button>}
                  {editingCaption && captionBusy === p.id && <span className="px-2 py-1 text-[10px] text-zinc-500" role="status">Saving…</span>}
                  {caption && <button type="button" disabled={captionBusy === p.id} onClick={() => clearCaption(p.id)} className="text-[10px] px-2 py-1 rounded border border-red-900/60 text-red-300 hover:bg-red-900/20 disabled:opacity-50"><Trash2 size={11} className="inline mr-1" />Clear</button>}
                  {editingCaption && <button type="button" onClick={() => setCaptionEditor(null)} className="text-[10px] px-2 py-1 rounded border border-zinc-700 text-zinc-300">Cancel</button>}
                </div>
                {(savedCaption.history || []).length > 0 && <details className="mt-2 text-[10px] text-zinc-500">
                  <summary className="cursor-pointer hover:text-zinc-300"><History size={11} className="inline mr-1" />Caption history ({savedCaption.history.length})</summary>
                  <div className="mt-2 max-h-36 space-y-2 overflow-y-auto">
                    {[...savedCaption.history].reverse().map((entry, index) => <div key={`${entry.updated_at}-${index}`} className="border-l border-zinc-700 pl-2">
                      <div>{entry.updated_at ? new Date(entry.updated_at).toLocaleString() : "Previous"}</div>
                      <p className="whitespace-pre-wrap text-zinc-400">{entry.caption}</p>
                    </div>)}
                  </div>
                </details>}
              </div>
            </div>
          );
        })}
      </div>
      <datalist id="mv-tier-options">{["VIP", "Free", "PPV", "Subscription", "Premium", "Store", "Inner Circle"].map((t) => <option key={t} value={t} />)}</datalist>
    </div>
  );
}
